import { useState } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  PipelineFunnelChart,
  RecruiterPerformanceTable,
  TimeToHireChart,
  SourceAnalysisChart,
  DateRangePicker,
} from '@/components/reports';
import { BarChart3, Users, Clock, Globe } from 'lucide-react';

function formatDate(date: Date): string {
  return date.toISOString().split('T')[0];
}

function getDefaultRange(): { start: string; end: string } {
  const end = formatDate(new Date());
  const startDate = new Date();
  startDate.setDate(startDate.getDate() - 30);
  return { start: formatDate(startDate), end };
}

export function ReportsPage() {
  const [activeTab, setActiveTab] = useState('pipeline');
  const defaultRange = getDefaultRange();
  const [startDate, setStartDate] = useState(defaultRange.start);
  const [endDate, setEndDate] = useState(defaultRange.end);

  const handleDateChange = (start: string, end: string) => {
    setStartDate(start);
    setEndDate(end);
  };

  // Pass undefined for "All time" (empty strings)
  const filterStart = startDate || undefined;
  const filterEnd = endDate || undefined;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-text-primary">Reports</h1>
        <p className="text-text-secondary mt-1">
          Analyze your recruitment pipeline, team performance, and hiring metrics.
        </p>
      </div>

      {/* Date Range Picker */}
      <DateRangePicker
        startDate={startDate}
        endDate={endDate}
        onChange={handleDateChange}
      />

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList>
          <TabsTrigger value="pipeline" className="flex items-center gap-2">
            <BarChart3 className="h-4 w-4" />
            Pipeline Funnel
          </TabsTrigger>
          <TabsTrigger value="performance" className="flex items-center gap-2">
            <Users className="h-4 w-4" />
            Recruiter Performance
          </TabsTrigger>
          <TabsTrigger value="time-to-hire" className="flex items-center gap-2">
            <Clock className="h-4 w-4" />
            Time to Hire
          </TabsTrigger>
          <TabsTrigger value="sources" className="flex items-center gap-2">
            <Globe className="h-4 w-4" />
            Source Analysis
          </TabsTrigger>
        </TabsList>

        <TabsContent value="pipeline" className="mt-6">
          <PipelineFunnelChart startDate={filterStart} endDate={filterEnd} />
        </TabsContent>

        <TabsContent value="performance" className="mt-6">
          <RecruiterPerformanceTable startDate={filterStart} endDate={filterEnd} />
        </TabsContent>

        <TabsContent value="time-to-hire" className="mt-6">
          <TimeToHireChart startDate={filterStart} endDate={filterEnd} />
        </TabsContent>

        <TabsContent value="sources" className="mt-6">
          <SourceAnalysisChart startDate={filterStart} endDate={filterEnd} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
